import React, {
  createContext, useCallback, useContext, useMemo,
} from "react"
import { BadgeElement, BadgeTemplate } from "sharedTypes/badge"
import {
  useDocument, useGetDocument, useSelectedIds, useSelection,
} from "components/CanvasEditor/Editor"
import useInlineEditing from "../hooks/useInlineEditing"
import useStableCallbacks from "../hooks/useStableCallbacks"

type InlineEditingState = {
  inlineEditingElementId?: string
  startInlineEditing: (elementId: string) => void
  stopInlineEditing: () => void
  setInlineEditingElementId: (id: string | undefined) => void
  /** Selects an element (toggles it with Shift), or deselects all; ends text editing. */
  setSelectedElementId: (id?: string, shiftKey?: boolean) => void
  handleDeselectElement: () => void
}

// Only the text editing state: it changes rarely, so the elements reading it don't re-render
// when the selection changes
const BadgeSelectionContext = createContext<InlineEditingState | null>(null)

/** Which text is being edited, and the actions that end it. */
export const useBadgeInlineEditing = () => {
  const context = useContext(BadgeSelectionContext)
  if (!context) throw new Error("useBadgeInlineEditing needs a BadgeSelectionProvider")

  return context
}

/** The selection, from the editor's selection store, with the text editing state. */
export const useBadgeSelection = () => {
  const inlineEditing = useBadgeInlineEditing()
  const selectedElementIds = useSelectedIds()
  const selectMultiple = useSelection((state) => state.setSelection)
  const template = useDocument<BadgeTemplate>()
  const selectedElements = useMemo<BadgeElement[]>(() => template.badgeData.elements.filter(
    (element) => selectedElementIds.includes(element.id),
  ), [template, selectedElementIds])

  return {
    ...inlineEditing,
    selectedElementIds,
    selectedElements,
    selectMultiple,
  }
}

// Export context for direct use in components that need optional access (e.g., preview mode)
export { BadgeSelectionContext }

export const BadgeSelectionProvider = ({ children }: { children: React.ReactNode }) => {
  const getTemplate = useGetDocument<BadgeTemplate>()
  const select = useSelection((state) => state.select)
  const deselectAll = useSelection((state) => state.clear)

  const selectOne = useCallback((id?: string, shiftKey = false) => {
    if (id) select(id, shiftKey)
    else deselectAll()
  }, [select, deselectAll])

  const {
    inlineEditingElementId,
    startInlineEditing,
    stopInlineEditing,
    setInlineEditingElementId,
  } = useInlineEditing(selectOne, getTemplate)

  const actions = useStableCallbacks({
    startInlineEditing,
    stopInlineEditing,
    setInlineEditingElementId,
    setSelectedElementId: (id?: string, shiftKey = false) => {
      if (inlineEditingElementId && id !== inlineEditingElementId) {
        // Deferred so the text field's blur saves the edit first
        setTimeout(() => setInlineEditingElementId(undefined), 0)
      }
      selectOne(id, shiftKey)
    },
    handleDeselectElement: () => {
      deselectAll()
      // Deferred so the text field's blur saves the edit first
      setTimeout(() => setInlineEditingElementId(undefined), 0)
    },
  })

  const value = useMemo(
    () => ({ ...actions, inlineEditingElementId }),
    [actions, inlineEditingElementId],
  )

  return (
    <BadgeSelectionContext.Provider value={value}>
      {children}
    </BadgeSelectionContext.Provider>
  )
}
